import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useUrlState } from '@/hooks/useUrlState';
import { ArrowLeft, Lock, PanelRightClose, PanelRightOpen } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { ProjectAttachmentsPanel } from '../components/ProjectAttachmentsPanel';
import { ProjectCyclesTab } from '../components/ProjectCyclesTab';
import { ProjectModulesTab } from '../components/ProjectModulesTab';
import { ProjectOverviewTab } from '../components/ProjectOverviewTab';
import { ProjectPagesTab } from '../components/ProjectPagesTab';
import { ProjectSettingsTab } from '../components/ProjectSettingsTab';
import { ProjectViewsTab } from '../components/ProjectViewsTab';
import { WorkItemsPanel } from '../components/WorkItemsPanel';
import { projectStatusLabel, projectStatusVariant } from '../constants';
import { useCanAdminProjects } from '../permissions';
import { useArchiveProject, useProject } from '../queries';

const TABS = ['Overview', 'Work Items', 'Modules', 'Cycles', 'Views', 'Pages', 'Attachments', 'Settings'] as const;
type Tab = (typeof TABS)[number];

// Tabs a guest (client) may see. Overview renders read-only for guests
// (progress + description, no side panel). Attachments and Settings stay
// staff-only; the rest are read/collaborate surfaces scoped server-side.
const GUEST_TABS: readonly Tab[] = ['Overview', 'Work Items', 'Modules', 'Cycles', 'Views', 'Pages'];

export function ProjectWorkspacePage() {
  const { publicId = '' } = useParams();
  const { data: project, isPending, isError } = useProject(publicId);
  // Active tab lives in the URL (?tab=) so it survives reload and the browser
  // Back/Forward + macOS back-swipe move between tabs. `push` for that history.
  const [tab, setTab] = useUrlState<Tab>('tab', 'Work Items', {
    push: true,
    deserialize: (r) => ((TABS as readonly string[]).includes(r) ? (r as Tab) : 'Work Items'),
  });
  const [showPanel, setShowPanel] = useState(true);
  const canAdmin = useCanAdminProjects();
  const isGuest = useSession((s) => s.role) === 'guest';
  const archive = useArchiveProject();

  const visibleTabs = TABS.filter((t) => {
    if (t === 'Settings') return canAdmin;
    if (isGuest) return GUEST_TABS.includes(t);
    return true;
  });
  // Coerce a guest who deep-links a staff-only tab (?tab=Attachments) back to a
  // safe one, so the content below never renders a staff-only panel for them.
  const shownTab: Tab = visibleTabs.includes(tab) ? tab : 'Work Items';

  if (isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }
  if (isError || !project) {
    return (
      <div className="rounded-md border border-dashed p-10 text-center">
        <p className="font-medium">Project not found</p>
        <p className="text-muted-foreground text-sm">
          It may be private, archived, or you don’t have access.
        </p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/projects">
            <ArrowLeft className="size-4" /> Back to projects
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/projects" aria-label="Back to projects">
              <ArrowLeft />
            </Link>
          </Button>
          <span className="text-2xl">{project.icon || '📁'}</span>
          <div>
            <h1 className="flex items-center gap-2 text-xl font-semibold tracking-tight">
              {project.name}
              {project.visibility === 'private' ? (
                <Lock className="text-muted-foreground size-4" />
              ) : null}
            </h1>
            <div className="flex items-center gap-2">
              {project.identifier ? (
                <Badge variant="outline" className="font-mono text-xs">
                  {project.identifier}
                </Badge>
              ) : null}
              <Badge variant={projectStatusVariant(project.status)}>
                {projectStatusLabel(project.status)}
              </Badge>
            </div>
          </div>
        </div>
        {canAdmin && !project.is_archived ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => archive.mutate(project.public_id)}
            disabled={archive.isPending}
          >
            Archive
          </Button>
        ) : null}
      </div>

      <div className="flex items-center gap-1 border-b">
        {/* Settings is config-only (non-admins); Overview/Attachments are
            staff-only for guests — server enforces too (F2 / ADR-004). */}
        {visibleTabs.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              shownTab === t
                ? 'border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground border-transparent',
            )}
          >
            {t}
          </button>
        ))}
        {shownTab === 'Overview' && !isGuest ? (
          <button
            type="button"
            onClick={() => setShowPanel((s) => !s)}
            aria-label={showPanel ? 'Hide properties' : 'Show properties'}
            className="text-muted-foreground hover:text-foreground ml-auto rounded-md p-1.5"
          >
            {showPanel ? <PanelRightClose className="size-5" /> : <PanelRightOpen className="size-5" />}
          </button>
        ) : null}
      </div>

      {shownTab === 'Overview' ? (
        <ProjectOverviewTab project={project} showPanel={showPanel} />
      ) : shownTab === 'Work Items' ? (
        <WorkItemsPanel project={project} />
      ) : shownTab === 'Modules' ? (
        <ProjectModulesTab project={project} />
      ) : shownTab === 'Cycles' ? (
        <ProjectCyclesTab project={project} />
      ) : shownTab === 'Views' ? (
        <ProjectViewsTab project={project} />
      ) : shownTab === 'Pages' ? (
        <ProjectPagesTab project={project} />
      ) : shownTab === 'Attachments' ? (
        <ProjectAttachmentsPanel projectId={project.id} kbSyncEnabled={project.kb_sync_enabled} />
      ) : shownTab === 'Settings' && canAdmin ? (
        <ProjectSettingsTab project={project} />
      ) : (
        <WorkItemsPanel project={project} />
      )}
    </div>
  );
}

